import React, { useState, lazy } from 'react';
import { useDebounce } from '@turbocomplynpm/react-debounce';
import classNames from 'classnames/bind';
import {
  useRecoilValueLoadable,
  useSetRecoilState,
} from '@src/libs/recoil/recoil';
import { useNavigate } from '@src/libs/router/react-router';
import SuspenseLoader from '@src/uikit/SuspenseLoader/suspense-loader';
import { getLoadingPageUrl } from '@src/libs/router/route-helper';
import { Select, Option } from '@src/uikit/select/select';
import { SvgChevronRight, SvgSearch } from '@turbocomplynpm/icons';
import { Flex } from '@turbocomplynpm/flex';
import { useCinHook } from '@src/report/use-cin-hook';
import { DEBOUNCE_TIMEOUT } from '@src/utils/global-constants';
import * as styles from './home-search.module.css';
import {
  companiesSearchAtom,
  companiesSearchSelector,
} from './atoms/home-atoms';

const SvgBackgroundGlow = lazy(() => import('@src/icons/svg-bg-glow'));

const cx = classNames.bind(styles);

export function HomeSearch() {
  const companiesInfo = useRecoilValueLoadable(companiesSearchSelector);
  const setSearchAtom = useSetRecoilState(companiesSearchAtom);
  const navigate = useNavigate();
  const [gstin, setGstin] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { handleSetCinAtom } = useCinHook();

  const handleGstinChange = (value: string | string[]) => {
    if (typeof value === 'string' && companiesInfo.state === 'hasValue') {
      const selectedCompany = companiesInfo.contents.companiesOptions.find(
        (option) => option.value === value,
      );
      const companyName = selectedCompany?.label ?? '';
      setGstin(value);
      handleSetCinAtom({
        cin: value,
        gst: [],
      });
      const pageUrl = getLoadingPageUrl({
        cin: value,
        companyName,
        gstins: [],
      });
      navigate(pageUrl);
    }
  };

  const handleOnSearch = (input: string) => {
    if (input) {
      setIsSearchOpen(true);
      setSearchAtom(input);
    } else {
      setIsSearchOpen(false);
      setSearchAtom('');
    }
  };

  const debouncedOnSearch = useDebounce(handleOnSearch, DEBOUNCE_TIMEOUT);

  return (
    <div className={cx('container')}>
      <SuspenseLoader loaderType="skeleton">
        <SvgBackgroundGlow className={cx('background-glow')} />
      </SuspenseLoader>
      <Select
        placeholder="Search for a company"
        className={cx('search-select')}
        value={gstin}
        onChange={handleGstinChange}
        showSearch
        onSearch={debouncedOnSearch}
        filterOption={false}
        open={isSearchOpen}
        suffixIcon={<SvgSearch />}
      >
        {companiesInfo.state === 'loading' && (
          <Option title="Loading..." value="loading">
            <Flex between row middle className={cx('option')}>
              Loading...
            </Flex>
          </Option>
        )}
        {companiesInfo.state === 'hasError' && (
          <Option title="Error" value="error">
            <Flex between row middle className={cx('option')}>
              Error fetching data try again
            </Flex>
          </Option>
        )}
        {companiesInfo.state === 'hasValue' &&
          companiesInfo.contents.companiesOptions.map((option) => (
            <Option
              key={option.value}
              title={option.label}
              value={option.value}
            >
              <Flex between row middle className={cx('option')}>
                {option.label}{' '}
                <SvgChevronRight size="medium" color="currentColor" />
              </Flex>
            </Option>
          ))}
      </Select>
    </div>
  );
}
